import { Component, computed, inject, input, Signal } from '@angular/core';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { NO_MEASURE, VbarSeriesComponent } from '../../../shared/dashboard/vbarchart-table/vbar-series/vbar-series.component';
import { VbarStyle } from '../../../shared/dashboard/vbarchart-table/vbar-batch/vbar-batch.component';
import { DisplayService } from '../../../services/display.service';
import { interventielaagDetails } from '../../../services/schoolaanwezigheid';
import { fromPairs, isUndefined, last } from 'lodash-es';
import { deelVeilig, generateCssClassForString, getSchooljaarTovHuidig, prettyCount, today } from '@cumlaude/shared-utils';
import { Level, Path, pathTo } from '../../../services/data-tree';
import { createSelection } from '../../../shared/dashboard/base-dashboard/base-dashboard-config';
import { Interventielaag } from '@cumlaude/metadata';
import { getFormattedValue } from '../../../shared/components/table/table/table.model';
import { BarInfo } from '../../../services/stacked-bars';
import { BasicFilterExpression, ExportFilter } from '../../../services/data.service';
import { AD_HOC_SELECTION_ID } from '../../../services/leerling-selectie.service';
import { UrlService } from '../../../services/url.service';
import { formatDate } from '@angular/common';
import { FilterService } from '../../../services/filter.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SchoolaanwezigheidA, SchoolaanwezigheidRequestContext } from '../aanwezigheid-overzicht.component';
import { ExportablePanel, TableExportPage } from '../../../services/exportable';
import { ExportOptions } from '../../../services/export.service';
import { ExportColumnDef } from '../../../shared/dashboard/data-tree-table/data-tree-table';
import { UserService } from '../../../services/user.service';

@Component({
	selector: 'app-aanwezigheidspiramide',
	templateUrl: './aanwezigheidspiramide.component.html',
	styleUrls: ['./aanwezigheidspiramide.component.scss'],
	imports: [IconComponent, TooltipDirective, VbarSeriesComponent],
})
export class AanwezigheidspiramideComponent implements ExportablePanel {
	protected readonly displayService = inject(DisplayService);
	private readonly urlService = inject(UrlService);
	private readonly filterService = inject(FilterService);
	private readonly userService = inject(UserService);

	readonly requestContext = input.required<SchoolaanwezigheidRequestContext>();

	interventielagen = Object.values(Interventielaag);
	readonly schooljaar: Signal<string | undefined> = toSignal(this.filterService.observeAsInput('aw_nm_schooljaar'));

	readonly subtitleTotaal = computed(() => {
		const schooljaar = this.schooljaar();
		if (schooljaar === getSchooljaarTovHuidig()) {
			return `Cumulatief t/m ${formatDate(today(), 'MMMM yyyy', 'nl_NL')}`;
		}

		return `Schooljaar ${schooljaar}`;
	});

	readonly totaalPartitions = computed(() => {
		const context = this.requestContext();
		const stacks: BarInfo[][] = (context.columnRoot?.r ?? []).map((path) => [
			{
				size: this.getTotaalLaagSize(path),
				className: generateCssClassForString(last(path)!.k),
				text: this.getLlnPercentageX(context.dataRoot!, path),
				selection: this.createTotaalLaagSelection(context, last(path)!.k as Interventielaag, this.getTotaalLaagSize(path)),
			},
		]);
		return [
			{
				qty: 100,
				stacks,
				measure: NO_MEASURE,
				label: '',
				path: context.dataRoot?.r[0] ?? [],
			},
		];
	});

	readonly totaalLagen = computed(() => {
		const { columnRoot } = this.requestContext();
		if (isUndefined(columnRoot)) return [];

		const laagPaths = fromPairs(columnRoot.r.map((path) => [last(path)!.k!, path]));
		return this.interventielagen.map((laag) => {
			const laagPath = laagPaths[laag];
			if (isUndefined(laagPath))
				return [
					{
						k: laag,
						i: 0,
						c: [],
						v: [],
						a: { aw_m_nr_leerlingen: 0, aw_m_nr_leerlingen_x: 0, aw_m_nr_minuten: 0, aw_m_nr_minuten_aanwezig: 0, count_records: 0 },
						r: [],
					},
				];

			return laagPath;
		});
	});

	protected getAantalText(path: Path<SchoolaanwezigheidA, number[]>) {
		const aantal = last(path)!.a.aw_m_nr_leerlingen_x;
		return `${prettyCount(aantal, 'leerling', 'leerlingen')} (${this.getLlnPercentageX(this.requestContext().dataRoot!, path)})`;
	}

	protected getLlnPercentageX(dataRoot: Level<SchoolaanwezigheidA, number[]>, path: Path<SchoolaanwezigheidA, number[]>) {
		return getFormattedValue(this.getLlnFractieX(path, dataRoot), 'percentage');
	}

	private getLlnFractieX(path: Level<SchoolaanwezigheidA, number[]>[], dataRoot: Level<SchoolaanwezigheidA, number[]>) {
		return deelVeilig(last(path)!.a.aw_m_nr_leerlingen_x, dataRoot.a.aw_m_nr_leerlingen);
	}

	protected getTotaalLaagSize(laagPath: Path<SchoolaanwezigheidA, number[]>) {
		return last(laagPath)!.a.aw_m_nr_leerlingen_x;
	}

	protected createTotaalLaagSelection(context: SchoolaanwezigheidRequestContext, laag: Interventielaag, size: number) {
		if (this.userService.isBestuur()) return undefined;

		const path = pathTo(context.dataRoot!);
		const x_leerlingselectie = JSON.stringify([{ id: AD_HOC_SELECTION_ID, naam: '' }]);

		const config = {
			detailUrl: `/aanwezigheid/overzicht?x_leerlingselectie=${x_leerlingselectie}`,
			mergeQueryParams: true,
			provider: 'schoolaanwezigheid',
			getSize: (_path: Path<SchoolaanwezigheidA, number[]>) => size,
			extraFilters: [new BasicFilterExpression(['aw_fun_nm_interventielaag'], laag)],
		};
		return createSelection(context, path, config);
	}

	protected navigateToTotaalLaag(laagPath: Path<SchoolaanwezigheidA, number[]>) {
		const selection = this.createTotaalLaagSelection(
			this.requestContext(),
			last(laagPath)!.k as Interventielaag,
			this.getTotaalLaagSize(laagPath)
		);
		this.urlService.navigateToSelection(selection);
	}

	async exportAsTable(exportOptions: ExportOptions, filters: ExportFilter[]): Promise<TableExportPage> {
		const columns: ExportColumnDef[] = [
			{ name: '', type: 'string' },
			{ name: 'Leerlingen', type: 'number' },
			{ name: '%', type: 'percentage', format: '1.0-0' },
		];
		return {
			name: 'Aanwezigheidspiramide',
			...(exportOptions.showTitle ? { titles: ['Aanwezigheidspiramide', this.subtitleTotaal()] } : {}),
			...(exportOptions.showFilters ? { filters } : {}),
			data: [
				{
					columns,
					rows: this.totaalLagen().map((laagPath) => {
						return [
							{ value: last(laagPath)!.k },
							{ value: this.getTotaalLaagSize(laagPath) },
							{ value: this.getLlnFractieX(laagPath, this.requestContext().dataRoot!) },
						];
					}),
				},
			],
		};
	}

	protected readonly Style = VbarStyle;
	protected readonly last = last;
	protected readonly generateCssClassForString = generateCssClassForString;
	protected readonly interventielaagDetails: Record<Interventielaag, string> = interventielaagDetails;
}
